/**
 * Yovo Assist empty-state copy + welcome quick actions.
 *
 * Edit this file to add / remove / reorder welcome CTAs — do not hardcode
 * chips inside Chatbot.jsx.
 *
 * Schema per action:
 *   id             stable key (used as React key)
 *   label          chip text shown in UI (emoji OK)
 *   sendText       user-side message recorded when tapped
 *   localBotReply  optional: inject this bot reply locally (no API call).
 *                  Use for intake openers — do NOT put destination lists here;
 *                  next user answers go through normal AI conversation.
 *   enabled        set false to hide without deleting (optional, default true)
 */

export const YOVO_ASSIST_WELCOME =
  "Hey! I'm Yovo Assist 👋\nHow can I help with your travel today?";

/** Prior welcome strings (migrate localStorage / detect welcome bubble) */
export const YOVO_ASSIST_WELCOME_LEGACY = [
  "Hey! I'm Yovo Assist 👋\n\nHow can I help with your travel today?",
  "Hey! I'm Yovo Assist 👋\nHow can I help with your travel today?",
];

/** Intake opener for “Suggest a destination” — questions only, no destination list */
export const YOVO_ASSIST_SUGGEST_DEST_REPLY =
  "Absolutely! 🌍 Tell me a little about your trip and I’ll suggest some destinations.\n\nWhere are you travelling from, what’s your budget, and how many days do you have?";

/** Intake opener for “Plan my next trip” — open questions only, no itinerary */
export const YOVO_ASSIST_PLAN_TRIP_REPLY =
  "Absolutely! 🗺️ Let’s plan your next trip.\n\nTo get started, tell me:\n\n📍 Where would you like to go?\n📅 When are you planning to travel?\n\nIf you haven't decided on a destination yet, that's totally fine — tell me your budget, trip duration, and what kind of experience you're looking for, and I'll suggest some options.";

/** Intake opener for “Find me the best flight” — open questions only, no search JSON */
export const YOVO_ASSIST_BEST_FLIGHT_REPLY =
  "Absolutely! ✈️ Let’s find you the best flight.\n\nTo get started, tell me:\n\n📍 Where are you flying from and to?\n📅 What are your travel dates?\n\nAlso let me know if it’s one-way or round-trip.\n\nOnce I have these details, I’ll help you find the best options.";

/** Booking help — direct to support email */
export const YOVO_ASSIST_BOOKING_HELP_REPLY =
  "Absolutely! Our support team can help you with your booking.\n\nPlease email us at support@yovotrip.com with your booking details and a brief description of the issue. Our YovoTrip support team will get back to you and assist you further.";

export function isYovoAssistWelcomeText(text) {
  const t = String(text || '');
  if (!t) return false;
  if (t === YOVO_ASSIST_WELCOME) return true;
  if (YOVO_ASSIST_WELCOME_LEGACY.includes(t)) return true;
  return /^Hey! I'm Yovo Assist/i.test(t) && /How can I help with your travel today\?/i.test(t);
}

/**
 * @type {{ id: string, label: string, sendText: string, localBotReply?: string, enabled?: boolean }[]}
 */
export const YOVO_ASSIST_QUICK_ACTIONS = [
  {
    id: 'booking_help',
    label: '🎫 Help with a booking',
    sendText: 'Help me with a booking',
    localBotReply: YOVO_ASSIST_BOOKING_HELP_REPLY,
  },
  {
    id: 'plan_trip',
    label: '🗺️ Plan my next trip',
    sendText: 'Plan my next trip',
    localBotReply: YOVO_ASSIST_PLAN_TRIP_REPLY,
  },
  {
    id: 'best_flight',
    label: '✈️ Find me the best flight',
    sendText: 'Find me the best flight',
    localBotReply: YOVO_ASSIST_BEST_FLIGHT_REPLY,
  },
  {
    id: 'suggest_dest',
    label: '🌍 Suggest a destination',
    sendText: 'Suggest a destination',
    localBotReply: YOVO_ASSIST_SUGGEST_DEST_REPLY,
  },
];

export function getEnabledQuickActions(actions = YOVO_ASSIST_QUICK_ACTIONS) {
  return (actions || []).filter((a) => a && a.id && a.label && a.sendText && a.enabled !== false);
}
